import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import axios from "axios";
import { Dialog, Button, Dropdown, TextInput, Image } from "hesus-ui-kit";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import ClipLoader from "react-spinners/ClipLoader";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";
import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  GridRow,
} from "./AddEditVat.styled";
import closeIcon from "@icons/ic-close-white.svg";

const AddEditVat = (props) => {
  const { t, id, isEditMode, title, onClose, updateRefeshStatus, selectedVat } =
    props;
  const [isVatCreating, setIsVatCreating] = useState(false);
  const [createVatAPIError, setCreateVatAPIError] = useState("");

  const generalParams = useGetGeneralParameters();

  const [formData, setFormData] = useState({
    label: {
      required: true,
      value: selectedVat?.label ?? "",
      error: "",
    },
    vatCalculationRate: {
      required: true,
      value: selectedVat?.calculationRate ?? "",
      error: "",
    },
    country: {
      required: true,
      value: selectedVat?.country?.id ?? "",
      error: "",
    },
    isActive: {
      required: true,
      value: selectedVat?.isActive ?? true,
      error: "",
    },
  });

  const onCountryChange = (e) => {
    setFormData({
      ...formData,
      country: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };
  const onLabelChange = (e) => {
    setFormData({
      ...formData,
      label: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onVatCalculationRateChange = (e) => {
    setFormData({
      ...formData,
      vatCalculationRate: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.country.value) {
      isFormValid = false;
      data.country.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.label.value) {
      isFormValid = false;
      data.label.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.vatCalculationRate.value) {
      isFormValid = false;
      data.vatCalculationRate.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (
      data.vatCalculationRate.value &&
      (data.vatCalculationRate.value < 0 || data.vatCalculationRate.value > 1)
    ) {
      isFormValid = false;
      data.vatCalculationRate.error = t("GENERAL_FORMAT_ERROR");
    }
    if (
      data.vatCalculationRate.value &&
      !/^-?\d*[.,]?\d{0,2}$/.test(data.vatCalculationRate.value)
    ) {
      isFormValid = false;
      data.vatCalculationRate.error = t("GENERAL_FORMAT_ERROR");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }

    return isFormValid;
  };

  const getFormData = () => {
    const data = {
      id: isEditMode ? selectedVat.id : "",
      label: formData.label.value,
      calculationRate: formData.vatCalculationRate.value,
      isActive: formData.isActive.value,
      country: JSON.stringify({
        id: formData.country.value,
      }),
    };
    return data;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      setIsVatCreating(true);
      setCreateVatAPIError("");
      // Create solution
      if (!id && !isEditMode) {
        axios
          .post("/general/vatRates", getFormData())
          .then(() => {
            setIsVatCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsVatCreating(false);
            setCreateVatAPIError(error.response?.data?.message ?? "");
          });
      }
      if (id && isEditMode) {
        axios
          .put(`/general/vatRates/${id}`, getFormData())
          .then(() => {
            setIsVatCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsVatCreating(false);
            setCreateVatAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  return (
    <Dialog width="30rem">
      <div>
        <HeaderWrapper>
          <Title>
            {title} {id ? "#" + id : ""}
          </Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close Product price popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <OptionsWrapper>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow column="1fr">
                    <Dropdown
                      id="product-country"
                      label={t("COUNTRY")}
                      name="country"
                      value={formData.country.value}
                      onChange={onCountryChange}
                      defaultOption={t("COUNTRY")}
                      options={generalParams.countryList}
                      labelStyles={
                        formData.country.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.country.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      required
                      error={formData.country.error}
                    />
                  </GridRow>

                  <GridRow column="1fr">
                    <TextInput
                      id="label"
                      type="text"
                      label={t("LABEL")}
                      isFloat
                      required
                      value={String(formData.label.value)}
                      onChange={(e) => onLabelChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.label.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.label.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.label.error}
                      placeholder={t("LABEL")}
                    />
                  </GridRow>
                  <GridRow column="1fr">
                    <TextInput
                      id="vat_calculation_rate"
                      type="text"
                      label={t("VAT_CALCULATION_RATE")}
                      isFloat
                      required
                      value={String(formData.vatCalculationRate.value)}
                      onChange={(e) => onVatCalculationRateChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.vatCalculationRate.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.vatCalculationRate.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.vatCalculationRate.error}
                      placeholder={t("VAT_CALCULATION_RATE")}
                    />
                  </GridRow>
                </div>
              </SectionContainer>
            </FlexRow>
          </OptionsWrapper>
        </FormBody>

        <ButtonWrapper>
          {createVatAPIError && <div>{createVatAPIError}</div>}
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isVatCreating && <ClipLoader size={15} color="#fff" />}
              {!isVatCreating && t("SUBMIT")}
            </>
          </Button>

          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditVat.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  title: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  updateRefeshStatus: PropTypes.func.isRequired,
};

AddEditVat.defaultProps = {
  id: null,
  isEditMode: false,
};
export default withTranslation()(AddEditVat);
